import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, GripVertical, Zap, X } from "lucide-react";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { PageLoader } from "../shared/LoadingSpinner";
import { EmptyState } from "../shared/EmptyState";
import { StatusBadge } from "../shared/Badge";
import { STATUSES } from "../../lib/utils";
import { toast } from "sonner";

const CATEGORY_OPTIONS = [
    { id: "general", label: "Général" },
    { id: "resolution", label: "Résolution" },
    { id: "attente", label: "Attente" },
    { id: "escalade", label: "Escalade" },
];

const TEMPLATE_VARS = [
    { key: "ticket_number", desc: "Numéro du ticket" },
    { key: "title", desc: "Titre du ticket" },
    { key: "requester_name", desc: "Nom du demandeur" },
    { key: "agent_name", desc: "Nom de l'agent" },
    { key: "priority", desc: "Priorité" },
    { key: "type", desc: "Type de ticket" },
];

const EMPTY_FORM = { name: "", message_template: "", status_change: "", is_internal: false, category: "general", order: 0, is_active: true };

export default function AdminMacros() {
    const [macros, setMacros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => { loadMacros(); }, []);

    const loadMacros = async () => {
        try {
            setLoading(true);
            const data = await api.getAllMacros();
            setMacros(data);
        } catch { toast.error("Erreur chargement macros"); }
        finally { setLoading(false); }
    };

    const openNew = () => {
        setEditing(null);
        setForm({ ...EMPTY_FORM, order: macros.length });
        setShowForm(true);
    };

    const openEdit = (macro) => {
        setEditing(macro.id);
        setForm({
            name: macro.name,
            message_template: macro.message_template,
            status_change: macro.status_change || "",
            is_internal: macro.is_internal,
            category: macro.category || "general",
            order: macro.order ?? 0,
            is_active: macro.is_active,
        });
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
        try {
            const payload = { ...form, status_change: form.status_change || null };
            if (editing) {
                await api.updateMacro(editing, payload);
                toast.success("Macro mise à jour");
            } else {
                await api.createMacro(payload);
                toast.success("Macro créée");
            }
            setShowForm(false);
            loadMacros();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        }
    };

    const handleDelete = async (macroId) => {
        if (!window.confirm("Supprimer cette macro ?")) return;
        try {
            await api.deleteMacro(macroId);
            toast.success("Macro supprimée");
            loadMacros();
        } catch { toast.error("Erreur suppression"); }
    };

    const insertVariable = (varKey) => {
        setForm((prev) => ({ ...prev, message_template: prev.message_template + `{{${varKey}}}` }));
    };

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-macros">
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Macros / Réponses rapides</h1>
                    <p className="page-subtitle">{macros.length} macro{macros.length !== 1 ? "s" : ""} configurée{macros.length !== 1 ? "s" : ""}</p>
                </div>
                <Button onClick={openNew} data-testid="new-macro-btn"><Plus className="w-4 h-4 mr-2" />Nouvelle macro</Button>
            </div>

            {/* Form */}
            {showForm && (
                <div className="card mb-6" data-testid="macro-form">
                    <div className="card-header flex items-center justify-between">
                        <h2 className="text-sm font-semibold">{editing ? "Modifier la macro" : "Nouvelle macro"}</h2>
                        <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="card-body space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Nom *</label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Prise en charge" data-testid="macro-name-input" />
                            </div>
                            <div>
                                <label className="form-label">Catégorie</label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger data-testid="macro-category-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="form-label">Message template</label>
                            <div className="flex flex-wrap gap-1 mb-2">
                                {TEMPLATE_VARS.map((v) => (
                                    <button key={v.key} type="button" onClick={() => insertVariable(v.key)}
                                        className="px-2 py-0.5 text-[10px] font-mono bg-gray-100 hover:bg-blue-100 hover:text-blue-700 border rounded transition-colors" title={v.desc}
                                        data-testid={`insert-var-${v.key}`}
                                    >{`{{${v.key}}}`}</button>
                                ))}
                            </div>
                            <Textarea value={form.message_template} onChange={(e) => setForm({ ...form, message_template: e.target.value })} className="min-h-[120px] font-mono text-sm" placeholder="Bonjour,\n\nVotre demande {{ticket_number}}..." data-testid="macro-template-input" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="form-label">Changement de statut</label>
                                <Select value={form.status_change || "none"} onValueChange={(v) => setForm({ ...form, status_change: v === "none" ? "" : v })}>
                                    <SelectTrigger data-testid="macro-status-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Aucun changement</SelectItem>
                                        {STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="form-label">Ordre d'affichage</label>
                                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} data-testid="macro-order-input" />
                            </div>
                            <div className="flex items-end gap-6 pb-1">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="checkbox" checked={form.is_internal} onChange={(e) => setForm({ ...form, is_internal: e.target.checked })} className="rounded border-gray-300" data-testid="macro-internal-checkbox" />
                                    Note interne
                                </label>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="rounded border-gray-300" data-testid="macro-active-checkbox" />
                                    Active
                                </label>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
                            <Button onClick={handleSave} data-testid="save-macro-btn">{editing ? "Enregistrer" : "Créer"}</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* List */}
            {macros.length === 0 ? (
                <div className="card">
                    <EmptyState icon={Zap} title="Aucune macro" description="Créez des macros pour accélérer le traitement des tickets"
                        action={<Button onClick={openNew}><Plus className="w-4 h-4 mr-2" />Créer une macro</Button>} />
                </div>
            ) : (
                <div className="card">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th className="w-8">#</th>
                                    <th>Nom</th>
                                    <th>Catégorie</th>
                                    <th>Statut cible</th>
                                    <th>Interne</th>
                                    <th>Active</th>
                                    <th className="w-24">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {macros.map((macro) => (
                                    <tr key={macro.id} data-testid={`macro-row-${macro.id}`}>
                                        <td className="text-gray-400 text-xs">{macro.order}</td>
                                        <td>
                                            <span className="font-medium text-gray-900">{macro.name}</span>
                                        </td>
                                        <td>
                                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{CATEGORY_OPTIONS.find((c) => c.id === macro.category)?.label || macro.category}</span>
                                        </td>
                                        <td>
                                            {macro.status_change ? <StatusBadge status={macro.status_change} /> : <span className="text-gray-400 text-sm">—</span>}
                                        </td>
                                        <td>
                                            {macro.is_internal ? <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Oui</span> : <span className="text-gray-400 text-sm">Non</span>}
                                        </td>
                                        <td>
                                            {macro.is_active ? <span className="w-2 h-2 bg-green-500 rounded-full inline-block" /> : <span className="w-2 h-2 bg-gray-300 rounded-full inline-block" />}
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => openEdit(macro)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" data-testid={`edit-macro-${macro.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                                <button onClick={() => handleDelete(macro.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-testid={`delete-macro-${macro.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
